// GENERATED FILE — DO NOT EDIT BY HAND. Produced by scripts/sync_learning_blocks.py from shared/learning-blocks/. Change the source there and re-run; CI fails on drift.
// Source: services/chat-widget/src/app/components/learning-block/orbi-loader.ts

/**
 * Orbi aus Zeichen — Laden und Reveal der Lernhilfen (seit 20.10.2026, abgestimmt
 * am Prototyp mit Marcel; Nachfolger des Pixel-Glitzers `pixel-reveal.ts`, der als
 * Variante bleibt). Alles ist Text auf einer Canvas — kein Video, kein WebGL:
 *
 *   Aufbau   Orbi setzt sich aus Zeichen zusammen (verrauschte Auflösung von innen
 *            nach außen), Wissenssymbole (∑ π √ λ …) ziehen aus dem Rand auf Bahnen.
 *   Laden    endlos ohne sichtbare Wiederholung: alle P s spiralt ein Symbol in
 *            Orbi, läuft als Welle über seine Haut (das Zeichen druckt sich ein) und
 *            taucht außen mit neuem Zeichen wieder auf; Orbi schaut dem nächsten
 *            nach und blinzelt.
 *   Reveal   reveal(ms): die übrigen Symbole werden eingesaugt, Lachaugen, Hüpfer;
 *            dann zerfällt Orbi in Zeichen und von seiner Mitte läuft ein Ring über
 *            die Karte — innen ist der Inhalt frei, an der Kante glitzert der Akzent.
 *
 * Orbis Augen sind Blockzeichen im selben Raster wie er (Sechstelblöcke U+1FB00…,
 * dazu ▌ ▐ █): je Zelle 2×3 Felder, so wird die Kapsel feiner als das Raster; der
 * Glanzpunkt ist ein ausgespartes Feld. Die Blöcke werden als Rechtecke in den
 * Atlas gezeichnet — genau die Form der Zeichen, aber unabhängig davon, ob die
 * Schrift sie hat (kaum eine hat sie).
 *
 * Gezeichnet über Glyphen-Atlanten (je Zeichen × Farbe einmal vorgerendert, dann
 * drawImage), ~30 fps, pausiert bei verborgenem Tab. Orbi und die Bahnen hängen an
 * der Ladehöhe (`stageHeight`), nicht an der Canvas: beim Reveal wächst die Canvas
 * auf die Inhaltshöhe, Orbi bleibt stehen. `still` (reduced motion): die Szene
 * steht, nur die Augen leben. Ohne 2D-Canvas → `null`, die Hülle zeigt ihre Fläche.
 */

// Aus aid-style.ts (statt learning-block-theme.ts): diese Datei läuft als Kopie
// auch in der Vorschau des Portal-Reiters „Lernhilfen“ (sync_learning_blocks.py).
import { parseHex as parseColor } from './aid-style';

export interface OrbiLoader {
  /** Aufdecken über `ms`; danach ist die Fläche durchsichtig. */
  reveal(ms: number): void;
  stop(): void;
}

type Rgb = [number, number, number];
type Vec3 = [number, number, number];

const FONT = 'ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, monospace';
const BG_RAMP = [...' .·:∙•'];
/** Ohne - und =: die lesen sich im feinen Raster als Querstreifen. */
const ORB_RAMP = [...' .:;+*o%#@'];
const LEARN = [...'∑π√∞∆λ∫×∂Ωθφαβγ?'];
const SPARK = [...'+·*'];
/** Tutor-Orbi (agent-orb, Tutor-Palette), für die weiße Karte abgedunkelt. */
const ORBI = {
  ramp: ['#c3b6ff', '#a99cff', '#8e84ff', '#7a7cf8', '#626cf0', '#4f52d6', '#3e37ae'],
  bandA: '#df5ad6',
  bandB: '#1aa9d0',
  eye: '#2e1b6b',
};
const BG_FONT = 8;
/** Schriftgröße von Orbis Raster in CSS-px (Prototyp: 4,5). */
const ORB_FONT = 4.5;
const FPS = 30;
/** Takt beim Laden: ab T0 alle P s ein Symbol, SPIRAL s lang spiralt es hinein. */
const P = 1.5;
const SPIRAL = 0.95;
const T0 = 2.0;

/** Reveal-Ablauf in Sekunden bei BASE s Gesamtdauer (wird auf `ms` skaliert). */
const BASE = 1.5;
const ABSORBED = 0.35;
const BURST_AT = 0.7;
const RING_AT = 0.72;
const RING_END = 1.45;
/** Anteil der Reveal-Dauer, ab dem der Ring den Inhalt freigibt. */
export const ORBI_RING_AT = RING_AT / BASE;
/** Variante „Pixel Glitter“ (ohne Orbi): nichts wird eingesaugt, der Ring läuft
 *  fast sofort los — gleiche Ringdauer wie bei Code Orbi. */
const GLITTER_RING_AT = 0.08;
export const GLITTER_RING_AT_SHARE = GLITTER_RING_AT / BASE;
/** Gesamtdauer des Reveals ohne Orbi, als Anteil der Orbi-Dauer (zum Skalieren). */
export const GLITTER_REVEAL_SHARE = (GLITTER_RING_AT + (RING_END - RING_AT)) / BASE;

interface Grid {
  n: number;
  cellW: number;
  cellH: number;
  x: Float32Array;
  y: Float32Array;
  h1: Float32Array;
  h2: Float32Array;
  h3: Float32Array;
}
interface Atlas {
  c: HTMLCanvasElement;
  cw: number;
  ch: number;
  cellW: number;
  cellH: number;
}
interface Atlases {
  bg: Atlas;
  orb: Atlas;
  eye: Atlas;
  symL: Atlas;
  symS: Atlas;
  spark: Atlas;
}
interface Orbit {
  a: number;
  b: number;
  tilt: number;
  phase: number;
  speed: number;
  spawn: number;
  sx: number;
  sy: number;
  big: boolean;
}
interface Sym {
  x: number;
  y: number;
  z: number;
  te: number;
  fade: number;
  g: number;
  big: boolean;
}
interface Ev {
  te: number;
  e: Vec3;
  g: number;
  big: boolean;
}
interface Orb {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  R: number;
}
interface Eyes {
  open: number;
  lx: number;
  ly: number;
  happy: number;
}
interface EyeGeo {
  ex: number;
  ey: number;
  hw: number;
  hh: number;
  gx: number;
  gy: number;
  gr: number;
  acy: number;
  ar: number;
  th: number;
}

const clamp = (x: number, a: number, b: number): number => (x < a ? a : x > b ? b : x);
const smooth = (e0: number, e1: number, x: number): number => {
  const u = clamp((x - e0) / (e1 - e0), 0, 1);
  return u * u * (3 - 2 * u);
};
/** Stabiler Pseudo-Zufall (gleiche Eingabe → gleiche Zahl). */
const hash = (a: number, b: number, s = 0): number => {
  const v = Math.sin(a * 127.1 + b * 311.7 + s * 74.7) * 43758.5453;
  return v - Math.floor(v);
};
const rgbOf = (c: string): Rgb => parseColor(c) ?? [0, 0, 0];
const hex = (c: Rgb): string => '#' + c.map((v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0')).join('');
/** Farbe `c` zu Anteil `t` auf Weiß. */
const onWhite = (c: Rgb, t: number): string => hex(c.map((v) => 255 + (v - 255) * t) as Rgb);

export function startOrbiLoader(
  canvas: HTMLCanvasElement,
  opts: {
    accent?: string | null;
    surface?: string | null;
    still?: boolean;
    stageHeight?: number;
    /** false = Variante „Pixel Glitter“: nur der Glitzer-Grund und der Ring aus
     *  der Kartenmitte — kein Orbi, keine Symbole, keine Funken. */
    orbi?: boolean;
  } = {},
): OrbiLoader | null {
  const ctx = canvas.getContext?.('2d');
  if (!ctx) return null;

  const ink: Rgb = parseColor(opts.accent) ?? [0, 53, 152];
  const surface = opts.surface?.trim() || '#ffffff';
  const still = !!opts.still;
  const withOrbi = opts.orbi !== false;
  const ringAt = withOrbi ? RING_AT : GLITTER_RING_AT;
  const ringEnd = ringAt + (RING_END - RING_AT);
  const stageH = opts.stageHeight ?? 132;
  // Orbi mittig in der Ladehöhe; ohne sichtbaren Ladetext darf er groß sein.
  const cy0 = stageH * 0.5;
  const R0 = Math.min(44, stageH * 0.33);

  let W = 0;
  let H = 0;
  let dpr = 0;
  let N = 8;
  let orbits: Orbit[] = [];
  let bg: Grid | null = null;
  let orbGrid: Grid | null = null;
  let atl: Atlases | null = null;

  const born = performance.now();
  let paused = 0;
  let pausedAt: number | null = null;
  let raf = 0;
  let last = 0;
  let stopped = false;
  let revealAt: number | null = null;
  let revealScale = 1;

  // ---------------------------------------------------------------- Atlanten & Raster
  const cellSize = (px: number): number => {
    ctx.font = `${px}px ${FONT}`;
    return ctx.measureText('M').width;
  };

  const atlas = (glyphs: string[], colors: string[], px: number, cellW: number, cellH: number): Atlas => {
    const c = document.createElement('canvas');
    const cw = Math.ceil(cellW * dpr);
    const ch = Math.ceil(cellH * dpr);
    c.width = cw * glyphs.length;
    c.height = ch * colors.length;
    const g = c.getContext('2d');
    if (g) {
      g.font = `${px * dpr}px ${FONT}`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      colors.forEach((col, r) => {
        g.fillStyle = col;
        glyphs.forEach((s, i) => g.fillText(s, i * cw + cw / 2, r * ch + ch / 2 + 0.5 * dpr));
      });
    }
    return { c, cw, ch, cellW, cellH };
  };

  /** Sechstelblöcke: Bit (2·Zeile + Spalte), Zeilen 0–2, Spalten 0–1 → 64 Muster.
   *  Kleiner Spalt wie der Zeichenabstand einer Schrift. */
  const blockAtlas = (cellW: number, cellH: number, color: string): Atlas => {
    const c = document.createElement('canvas');
    const cw = Math.ceil(cellW * dpr);
    const ch = Math.ceil(cellH * dpr);
    c.width = cw * 64;
    c.height = ch;
    const g = c.getContext('2d');
    if (g) {
      g.fillStyle = color;
      const gap = Math.max(0.5, 0.3 * dpr);
      const sw = cw / 2;
      const sh = ch / 3;
      for (let m = 1; m < 64; m++) {
        for (let b = 0; b < 6; b++) {
          if (!(m & (1 << b))) continue;
          const col = b & 1;
          const row = b >> 1;
          g.fillRect(m * cw + col * sw + (col ? 0 : gap / 2), row * sh + (row ? 0 : gap / 2), sw - gap / 2, sh - gap / 3);
        }
      }
    }
    return { c, cw, ch, cellW, cellH };
  };

  const grid = (cellW: number, cellH: number): Grid => {
    const cols = Math.ceil(W / cellW) + 1;
    const rows = Math.ceil(H / cellH) + 1;
    const n = cols * rows;
    const ox = (W - (cols - 1) * cellW) / 2;
    const oy = (H - (rows - 1) * cellH) / 2;
    const g: Grid = {
      n,
      cellW,
      cellH,
      x: new Float32Array(n),
      y: new Float32Array(n),
      h1: new Float32Array(n),
      h2: new Float32Array(n),
      h3: new Float32Array(n),
    };
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c;
        g.x[i] = ox + c * cellW;
        g.y[i] = oy + r * cellH;
        g.h1[i] = hash(c, r, 1);
        g.h2[i] = hash(c, r, 2);
        g.h3[i] = hash(c, r, 3);
      }
    }
    return g;
  };

  const buildAtlases = (bgCell: [number, number], orbCell: [number, number]): Atlases => {
    const gray: Rgb = [141, 154, 176];
    const accentLight = onWhite(ink, 0.5);
    return {
      bg: atlas(
        BG_RAMP,
        [
          onWhite(gray, 0.22), onWhite(gray, 0.34), onWhite(gray, 0.48), onWhite(gray, 0.62),
          onWhite(ink, 0.3), onWhite(ink, 0.55), onWhite(ink, 0.8), hex(ink),
          onWhite(rgbOf(ORBI.ramp[2]), 0.35), onWhite(rgbOf(ORBI.ramp[2]), 0.6),
        ],
        BG_FONT,
        bgCell[0],
        bgCell[1],
      ),
      orb: atlas(
        [...ORB_RAMP, ...LEARN],
        [...ORBI.ramp, ORBI.bandA, ORBI.bandB, hex(ink), onWhite(ink, 0.6)],
        ORB_FONT,
        orbCell[0],
        orbCell[1],
      ),
      eye: blockAtlas(orbCell[0], orbCell[1], ORBI.eye),
      symL: atlas(LEARN, [hex(ink), accentLight], 12.5, 16, 16),
      symS: atlas(LEARN, [hex(ink), accentLight], 9.5, 13, 13),
      spark: atlas(SPARK, [hex(ink), ORBI.bandB], 9, 10, 10),
    };
  };

  const makeOrbits = (): Orbit[] => {
    N = clamp(Math.round(W / 46), 6, 12);
    const out: Orbit[] = [];
    for (let i = 0; i < N; i++) {
      const aMax = W / 2 - 14;
      const aMin = Math.min(R0 * 1.75, aMax);
      const a = aMin + (aMax - aMin) * (0.15 + 0.85 * hash(i, 7, 9));
      const ang = hash(i, 4, 9) * Math.PI * 2;
      out.push({
        a,
        // flache Bahnen, die in der Ladehöhe bleiben
        b: clamp(a * (0.2 + 0.16 * hash(i, 8, 9)), R0 * 0.75, stageH - cy0 - 12),
        tilt: (hash(i, 5, 9) - 0.5) * 0.3,
        phase: hash(i, 6, 9) * Math.PI * 2,
        speed: (0.55 + 0.4 * hash(i, 3, 9)) * (hash(i, 2, 9) > 0.25 ? 1 : -1),
        spawn: 0.15 + i * 0.07,
        sx: Math.cos(ang) * W * 0.6,
        sy: Math.sin(ang) * stageH * 0.7,
        big: hash(i, 1, 9) > 0.45,
      });
    }
    return out;
  };

  /** Größe übernehmen; false = (noch) nichts zu zeichnen. */
  const layout = (): boolean => {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const d = Math.min(window.devicePixelRatio || 1, 2);
    if (w === W && h === H && d === dpr && atl) return W > 0 && H > 0;
    const widthChanged = w !== W || d !== dpr || !atl;
    W = w;
    H = h;
    dpr = d;
    if (!W || !H) return false;
    canvas.width = Math.max(1, Math.round(W * dpr));
    canvas.height = Math.max(1, Math.round(H * dpr));
    const bgCell: [number, number] = [cellSize(BG_FONT) * 1.25, 9.5];
    const orbCell: [number, number] = [cellSize(ORB_FONT) * 1.02, ORB_FONT * 1.13];
    bg = grid(bgCell[0], bgCell[1]);
    orbGrid = grid(orbCell[0], orbCell[1]);
    if (widthChanged) {
      orbits = makeOrbits();
      atl = buildAtlases(bgCell, orbCell);
    }
    return true;
  };

  const put = (a: Atlas, gi: number, ci: number, x: number, y: number): void => {
    ctx.drawImage(a.c, gi * a.cw, ci * a.ch, a.cw, a.ch, x - a.cellW / 2, y - a.cellH / 2, a.cellW, a.cellH);
  };

  // ---------------------------------------------------------------- Choreografie
  /** u = Sekunden seit reveal() in Basis-Zeit (−1 = lädt noch). */
  const orbState = (t: number, u: number): Orb => {
    let cy = cy0;
    let rx = R0;
    let ry = R0;
    if (!still) {
      const br = 1 + 0.015 * Math.sin(t * 2.4);
      rx *= br;
      ry *= br;
      const v = (u - ABSORBED) / 0.4; // Hüpfer; höher ginge über den Kartenrand
      if (u >= 0 && v >= 0 && v <= 1) {
        cy -= R0 * 0.3 * Math.sin(Math.PI * v);
        const s = 0.07 * Math.sin(Math.PI * v);
        rx *= 1 - s;
        ry *= 1 + s;
      }
      for (const [t0, amp] of [
        [0.24, 0.09],
        [0.75, 0.07],
      ]) {
        const w = (u - t0) / 0.18; // Stauchen: Absprung, Landung
        if (u >= 0 && w >= 0 && w <= 1) {
          const s = amp * Math.sin(Math.PI * w);
          rx *= 1 + s;
          ry *= 1 - s;
          cy += R0 * s;
        }
      }
    }
    return { cx: W / 2, cy, rx, ry, R: (rx + ry) / 2 };
  };

  const symbols = (t: number, o: Orb, u: number): Sym[] => {
    const out: Sym[] = [];
    if (u >= ABSORBED) return out;
    for (let i = 0; i < N; i++) {
      const p = orbits[i];
      const m = Math.max(0, Math.ceil((t - T0 - i * P) / (N * P))); // aktuelles „Leben“ des Platzes
      const te = T0 + i * P + m * N * P;
      const birth = m === 0 ? p.spawn : te - N * P;
      let s = still ? 0 : smooth(te - SPIRAL, te, t);
      if (u >= 0) {
        if (revealAt !== null && te - SPIRAL > revealAt) s = 0;
        s = Math.max(s, smooth(0, ABSORBED, u));
      }
      const fade =
        (m === 0 ? smooth(p.spawn, p.spawn + 0.6, t) : smooth(birth, birth + 0.8, t)) * (1 - smooth(0.72, 0.95, s));
      const grow = m === 0 ? 1 : 1 + 0.3 * (1 - smooth(birth, birth + 0.9, t));
      const th = p.phase + p.speed * t + 2.2 * s * Math.sign(p.speed);
      const a = p.a * grow * (1 - s) + o.R * 0.4 * s;
      const b = p.b * grow * (1 - s) + o.R * 0.3 * s;
      const px0 = a * Math.cos(th);
      const py0 = b * Math.sin(th);
      const c = Math.cos(p.tilt);
      const sn = Math.sin(p.tilt);
      const px = px0 * c - py0 * sn;
      const py = px0 * sn + py0 * c;
      const conv = smooth(p.spawn, p.spawn + 1.1, t);
      const x = o.cx + p.sx * (1 - conv) + px * conv;
      const y = o.cy + p.sy * (1 - conv) + py * conv;
      const edge = smooth(0, 16, Math.min(x, W - x)) * smooth(0, 8, Math.min(y, stageH - y));
      out.push({ x, y, z: Math.sin(th) * conv, te, fade: fade * edge, g: Math.floor(hash(i, m, 3) * LEARN.length), big: p.big });
    }
    return out;
  };

  const events = (t: number, u: number): Ev[] => {
    const ev: Ev[] = [];
    if (still) return ev;
    const n1 = Math.floor((t - T0) / P);
    for (let n = Math.max(0, n1 - 1); n <= n1; n++) {
      const te = T0 + n * P;
      if (te > t || t - te > 1.6 || (revealAt !== null && te - SPIRAL > revealAt)) continue;
      const i = n % N;
      const m = Math.floor(n / N);
      const p = orbits[i];
      const th = p.phase + p.speed * te + 2.2 * Math.sign(p.speed);
      const e: Vec3 = [Math.cos(th) * 0.8, Math.sin(th) * 0.35, 0.5];
      const l = Math.hypot(e[0], e[1], e[2]);
      ev.push({ te, e: [e[0] / l, e[1] / l, e[2] / l], g: Math.floor(hash(i, m, 3) * LEARN.length), big: false });
    }
    if (u >= ABSORBED && revealAt !== null) ev.push({ te: revealAt + ABSORBED * revealScale, e: [0, 0, 1], g: 0, big: true });
    return ev;
  };

  const eyes = (t: number, u: number, o: Orb, syms: Sym[]): Eyes => {
    let open = smooth(0.7, 0.95, t);
    const j = Math.floor((t - 1.6) / 3.1); // Blinzeln, leicht unregelmäßig
    for (const k of [j - 1, j]) {
      const tb = 1.6 + k * 3.1 + hash(k, 2, 5) * 0.9;
      const b = (t - tb) / 0.16;
      if (k >= 0 && b >= 0 && b <= 1) open *= 0.08 + 0.92 * Math.abs(1 - 2 * b);
    }
    let lx = 0.45 * Math.sin(t * 0.7);
    let ly = 0.2 * Math.sin(t * 0.45 + 1);
    if (!still) {
      // dem nächsten Symbol nachschauen
      let next: Sym | null = null;
      for (const sy of syms) if (sy.te > t && (!next || sy.te < next.te)) next = sy;
      if (next) {
        const w = smooth(next.te - 1.3, next.te - 0.7, t);
        const tx = clamp((next.x - o.cx) / (W * 0.3), -1, 1);
        const ty = clamp((next.y - o.cy) / (stageH * 0.4), -1, 1) * 0.7;
        lx += (tx - lx) * w;
        ly += (ty - ly) * w;
      }
    }
    const happy = u >= 0 ? smooth(0.22, 0.32, u) : 0;
    if (u >= 0) {
      const k = smooth(0.12, 0.3, u);
      lx *= 1 - k;
      ly *= 1 - k;
    }
    return { open, lx, ly, happy };
  };

  // ---------------------------------------------------------------- Augen (Blockzeichen)
  const eyeGeom = (o: Orb, e: Eyes): EyeGeo[] =>
    [-1, 1].map((side) => {
      const R = o.R;
      const ex = o.cx + (side * 0.28 + e.lx * 0.2) * o.rx;
      const ey = o.cy + (-0.02 + e.ly * 0.16) * o.ry;
      return {
        ex,
        ey,
        hw: 0.118 * R * (1 - 0.18 * Math.max(0, e.lx * side)),
        hh: 0.3 * R * e.open,
        gx: ex - 0.01 * R,
        gy: ey - 0.13 * R * e.open,
        gr: Math.max(1.3, 0.05 * R),
        acy: ey + 0.1 * R,
        ar: 0.13 * R,
        th: Math.max(2, 0.1 * R),
      };
    });

  /** Abstand zum Augenrand (< 0 = innen): Kapsel, beim Lachen der Bogen ∩. */
  const eyeDist = (x: number, y: number, k: EyeGeo, e: Eyes): number => {
    if (e.happy >= 0.5) {
      const d = Math.abs(Math.hypot(x - k.ex, y - k.acy) - k.ar) - k.th / 2;
      return y > k.acy + k.th * 0.3 ? Math.max(d, y - k.acy - k.th * 0.3) : d;
    }
    if (e.open <= 0.04) return 1e9;
    const qy = Math.max(Math.abs(y - k.ey) - Math.max(k.hh - k.hw, 0), 0);
    return Math.hypot(x - k.ex, qy) - Math.min(k.hw, k.hh);
  };
  const inEye = (x: number, y: number, k: EyeGeo, e: Eyes): boolean =>
    eyeDist(x, y, k, e) < 0 && !(e.happy < 0.5 && e.open > 0.6 && Math.hypot(x - k.gx, y - k.gy) < k.gr);

  /** Sechstel-Maske einer Zelle (Bits 0–5); Bit 64 = Zelle liegt am Auge (heller Saum). */
  const eyeMask = (x: number, y: number, geo: EyeGeo[], e: Eyes, cw: number, ch: number): number => {
    const dx = cw / 4;
    const dy = ch / 3;
    let m = 0;
    for (const k of geo) {
      const d = eyeDist(x, y, k, e);
      if (d > cw + ch) continue;
      for (let b = 0; b < 6; b++) {
        if (inEye(x + (b & 1 ? dx : -dx), y + ((b >> 1) - 1) * dy, k, e)) m |= 1 << b;
      }
      if (d < cw * 0.9) m |= 64;
    }
    return m;
  };

  // ---------------------------------------------------------------- Ebenen
  const drawBg = (t: number, o: Orb, mat: number, ring: number): void => {
    if (!bg || !atl) return;
    const g = bg;
    const a = atl.bg;
    const intro = still ? 1 : smooth(0, 0.7, t);
    const tf = Math.floor(t * 8);
    for (let i = 0; i < g.n; i++) {
      const x = g.x[i];
      const y = g.y[i];
      const d = Math.hypot(x - o.cx, y - o.cy);
      if (withOrbi && d < o.R * 1.02 && ring < 0) continue; // Orbi deckt den Grund
      if (ring >= 0 && d < ring - 4) continue; // aufgedeckt
      const nx = x / 150;
      const ny = y / 150;
      const wx = nx + 0.5 * Math.sin(ny * 1.7 + t * 0.35) + 0.22 * Math.sin(ny * 3.9 - t * 0.6);
      const wy = ny + 0.4 * Math.sin(nx * 1.3 - t * 0.28);
      const n =
        0.5 +
        0.5 *
          (0.55 * Math.sin(wx * 1.9 + t * 0.22) * Math.sin(wy * 2.3 - t * 0.17) +
            0.3 * Math.sin(wx * 4.7 - wy * 3.1 + t * 0.5) +
            0.15 * Math.sin(wx * 9.1 + wy * 7.3 - t * 0.9));
      const v = smooth(0.42, 0.95, n) * intro + (hash(i, tf, 4) - 0.5) * 0.14;
      const halo = withOrbi ? Math.exp(-Math.max(d - o.R, 0) / 26) * mat * (ring < 0 ? 1 : 0) : 0;
      const wave =
        ring >= 0 ? Math.exp(-((((d - ring) + 5 * Math.sin(Math.atan2(y - o.cy, x - o.cx) * 6)) / 9) ** 2)) : 0;
      if (v < 0.16 && halo < 0.3 && wave < 0.15) continue;
      let gi: number;
      let ci: number;
      if (wave > 0.15) {
        gi = g.h2[i] > 0.5 ? 5 : 4;
        ci = wave > 0.6 ? 7 : wave > 0.35 ? 6 : 5;
      } else if (halo > 0.3 && g.h2[i] < halo * 0.9) {
        gi = 1 + Math.floor(clamp(halo, 0, 0.99) * 3);
        ci = halo > 0.6 ? 9 : 8;
      } else {
        gi = 1 + Math.floor(clamp(v, 0, 0.999) * (BG_RAMP.length - 1));
        ci = g.h1[i] > 0.95 ? 4 : Math.min(3, Math.floor(clamp(v, 0, 0.999) * 4));
      }
      put(a, gi, ci, x, y);
    }
  };

  const drawOrb = (t: number, u: number, o: Orb, mat: number, ev: Ev[], eye: Eyes): void => {
    if (!orbGrid || !atl) return;
    const burst = u >= BURST_AT ? smooth(BURST_AT, 0.98, u) : 0;
    if (burst >= 1) return;
    const g = orbGrid;
    const a = atl.orb;
    const nR = ORB_RAMP.length;
    const L: Vec3 = [-0.45, -0.6, 0.66];
    const ll = Math.hypot(L[0], L[1], L[2]);
    const band = (tilt: number, spin: number): Vec3 => [
      Math.sin(tilt) * Math.cos(spin),
      Math.cos(tilt),
      Math.sin(tilt) * Math.sin(spin),
    ];
    const A = band(0.95, t * 0.9);
    const B = band(-1.25, -t * 0.65 + 1.3);
    let flash = 0;
    for (const e of ev) flash += Math.exp(-(t - e.te) * 7) * (e.big ? 0.8 : 0.3);
    const learnedSlot = Math.floor(t / 5);
    const glyphEyes = mat > 0.95 && (eye.open > 0.04 || eye.happy >= 0.5);
    const geo = glyphEyes ? eyeGeom(o, eye) : [];

    for (let i = 0; i < g.n; i++) {
      const dx = (g.x[i] - o.cx) / o.rx;
      const dy = (g.y[i] - o.cy) / o.ry;
      const r2 = dx * dx + dy * dy;
      if (r2 >= 1) continue;
      const rr = Math.sqrt(r2);
      const edge = mat * 1.35 - g.h1[i] * 0.35;
      if (rr > edge) continue;

      let atlasRef = a;
      let gi = 0;
      let ci = 0;
      const mask = glyphEyes ? eyeMask(g.x[i], g.y[i], geo, eye, g.cellW, g.cellH) : 0;
      if (mask & 63) {
        atlasRef = atl.eye;
        gi = mask & 63;
      } else if (mask & 64 && hash(i, 3, 3) < 0.6) {
        continue; // heller Saum ums Auge, wie der Glow der Agent-Kugel
      } else {
        const nz = Math.sqrt(1 - r2);
        const lam = Math.max(0, (dx * L[0] + dy * L[1] + nz * L[2]) / ll);
        const rim = (1 - nz) * (1 - nz);
        const spec = Math.exp(-((dx + 0.36) ** 2 + (dy + 0.44) ** 2) / 0.035);
        const bA = Math.exp(-(((dx * A[0] + dy * A[1] + nz * A[2]) / 0.17) ** 2)) * (0.55 + 0.45 * nz);
        const bB = Math.exp(-(((dx * B[0] + dy * B[1] + nz * B[2]) / 0.13) ** 2)) * (0.55 + 0.45 * nz);
        const inkv =
          0.16 + 0.5 * (1 - lam) + 0.4 * rim + 0.3 * smooth(0.78, 0.97, rr) + 0.22 * Math.max(bA, bB) - 0.75 * spec + flash * 0.25;
        gi = 1 + Math.floor(clamp(inkv, 0, 0.999) * (nR - 1));
        if (rr > edge - 0.22) gi = Math.min(gi, 3); // beim Aufbau erst lockere Zeichen
        if (spec > 0.55) gi = Math.min(gi, 1);
        ci = Math.round(clamp(Math.hypot(dx + 0.3, dy + 0.54) / 1.55, 0, 1) * 6);
        if (bA > 0.55) ci = 7;
        else if (bB > 0.6) ci = 8;
        // Wellen der gelernten Zeichen: das eingesaugte Symbol druckt sich in die Haut
        let rip = 0;
        let rg = 0;
        for (const e of ev) {
          const age = t - e.te;
          const ang = Math.acos(clamp(dx * e.e[0] + dy * e.e[1] + nz * e.e[2], -1, 1));
          const wv = Math.exp(-(((ang - age * 2.6) / (e.big ? 0.3 : 0.2)) ** 2)) * Math.exp(-age * 1.6);
          if (wv > rip) {
            rip = wv;
            rg = e.big ? Math.floor(g.h3[i] * LEARN.length) : e.g;
          }
        }
        if (rip > 0.45) {
          gi = nR + rg;
          ci = 9;
        } else if (!still && t > T0 && hash(i, learnedSlot, 6) < 0.1 && inkv > 0.3 && inkv < 0.8) {
          gi = nR + Math.floor(g.h3[i] * LEARN.length);
          ci = 10;
        }
      }

      let x = g.x[i];
      let y = g.y[i];
      if (burst > 0) {
        // Orbi zerfällt in Zeichen, die nach außen fliegen
        const ang = rr > 0.05 ? Math.atan2(dy, dx) : g.h2[i] * Math.PI * 2;
        const dist = (0.35 + 0.9 * g.h2[i]) * Math.pow(burst, 1.4) * 80;
        x += Math.cos(ang) * dist;
        y += Math.sin(ang) * dist;
        ctx.globalAlpha = 1 - smooth(0.25 + 0.55 * g.h1[i], 1, burst);
      }
      put(atlasRef, gi, ci, x, y);
    }
    ctx.globalAlpha = 1;
  };

  const drawSymbols = (syms: Sym[], o: Orb, front: boolean): void => {
    if (!atl) return;
    for (const s of syms) {
      if ((s.z >= 0) !== front || s.fade <= 0.01) continue;
      if (!front && Math.hypot(s.x - o.cx, s.y - o.cy) < o.R * 0.98) continue;
      ctx.globalAlpha = s.fade * (front ? 1 : 0.85);
      put(s.big && front ? atl.symL : atl.symS, s.g, front ? 0 : 1, s.x, s.y);
    }
    ctx.globalAlpha = 1;
  };

  const drawSparks = (t: number, o: Orb, ev: Ev[]): void => {
    if (!atl) return;
    for (const e of ev) {
      const age = t - e.te;
      if (age > 0.55) continue;
      const px = o.cx + e.e[0] * o.rx * 0.9;
      const py = o.cy + e.e[1] * o.ry * 0.9;
      const k = e.big ? 10 : 5;
      for (let j = 0; j < k; j++) {
        const ang = hash(e.te * 10, j, 8) * Math.PI * 2;
        const sp = (e.big ? 70 : 35) + 45 * hash(j, e.te * 10, 9);
        ctx.globalAlpha = 1 - age / 0.55;
        put(atl.spark, j % 3, j % 2, px + Math.cos(ang) * sp * age, py + Math.sin(ang) * sp * age - 12 * age);
      }
    }
    ctx.globalAlpha = 1;
  };

  /** Ring als Auflösekante: innen wird die Fläche durchsichtig (weicher Rand). */
  const clearRing = (o: Orb, r: number): void => {
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    const grad = ctx.createRadialGradient(o.cx, o.cy, Math.max(0, r - 16), o.cx, o.cy, Math.max(1, r));
    grad.addColorStop(0, 'rgba(0,0,0,1)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(o.cx, o.cy, Math.max(1, r), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  // ---------------------------------------------------------------- Ablauf
  const now = (): number => (performance.now() - born - paused) / 1000;

  const render = (t: number): void => {
    if (!layout()) return;
    const u = revealAt === null ? -1 : (t - revealAt) / revealScale;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, W, H);
    if (u >= ringEnd) return; // aufgedeckt
    const tA = still ? 3.6 : t;
    const mat = still ? 1 : smooth(0.05, 0.9, t);
    // Ohne Orbi bleibt der Ringmittelpunkt in der Mitte der Ladefläche.
    const o = withOrbi ? orbState(t, u) : { ...orbState(t, -1), cx: W / 2, cy: cy0 };
    let ring = -1;
    if (u >= 0) {
      // Beim Reveal deckt die Canvas den Inhalt selbst ab (die Hülle nimmt ihre Fläche weg).
      ctx.fillStyle = surface;
      ctx.fillRect(0, 0, W, H);
      if (u >= ringAt) {
        const k = clamp((u - ringAt) / (ringEnd - ringAt), 0, 1);
        const far = Math.max(Math.hypot(o.cx, o.cy), Math.hypot(W - o.cx, o.cy), Math.hypot(o.cx, H - o.cy), Math.hypot(W - o.cx, H - o.cy));
        ring = (far + 24) * (1 - Math.pow(1 - k, 3));
        clearRing(o, ring);
      }
    }
    if (!withOrbi) {
      drawBg(tA, o, mat, ring);
      return;
    }
    const ev = events(tA, u);
    const syms = symbols(tA, o, u);
    drawBg(tA, o, mat, ring);
    drawSymbols(syms, o, false);
    drawOrb(tA, u, o, mat, ev, eyes(still ? Math.max(t, 1.2) : t, u, o, syms));
    drawSymbols(syms, o, true);
    drawSparks(tA, o, ev);
  };

  const loop = (ts: number): void => {
    if (stopped) return;
    raf = requestAnimationFrame(loop);
    if (document.hidden || ts - last < 1000 / FPS - 2) return;
    last = ts;
    const t = now();
    render(t);
    if (revealAt !== null && (t - revealAt) / revealScale >= ringEnd) cancelAnimationFrame(raf);
  };

  const onVisibility = (): void => {
    if (document.hidden) pausedAt = performance.now();
    else if (pausedAt !== null) {
      paused += performance.now() - pausedAt;
      pausedAt = null;
    }
  };
  document.addEventListener('visibilitychange', onVisibility);
  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => !stopped && render(now())) : null;
  ro?.observe(canvas);
  raf = requestAnimationFrame(loop);

  return {
    reveal(ms: number): void {
      if (stopped || revealAt !== null) return;
      revealAt = now();
      revealScale = Math.max(0.3, ms / 1000 / BASE);
    },
    stop(): void {
      stopped = true;
      cancelAnimationFrame(raf);
      ro?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      atl = null;
    },
  };
}
